import { useCallback, useEffect, useState } from "react";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import './MapLocationPicker.css';

const GOOGLE_MAP_API_KEY = process.env.REACT_APP_GOOGLE_MAP_KEY || ""

const containerStyle = {
  height: '400px',
  position: "initial"
};

// Default location (Delhi) if no stored location found
const defaultCenter = {
  lat: 28.6139,
  lng: 77.2090
};

const CheckoutSectionMapLocationPicker = ({ formValue, setFormValues, setIsPickedOrSelectLocation, setErr }) => {
  const [markerPosition, setMarkerPosition] = useState(null);

  // Update marker position when formValue's lat/lng changes
  useEffect(() => {
    if (formValue?.latitude && formValue?.longitude) {
      setMarkerPosition({ lat: formValue.latitude, lng: formValue.longitude });
    } else {
      setMarkerPosition(null);
    }
  }, [formValue?.latitude, formValue?.longitude]);

  // Get stored location from sessionStorage or fallback country lat/lng
  const getStoredLocation = () => {
    const countryLatLngMap = {
      'India': { lat: 28.6139, lng: 77.2090 },
      'Thailand': { lat: 15.8700, lng: 100.9925 },
      'USA': { lat: 37.0902, lng: -95.7129 },
      'UK': { lat: 55.3781, lng: -3.4360 },
      'Canada': { lat: 56.1304, lng: -106.3468 },
    };
    try {
      const location = sessionStorage.getItem('location');
      if (location) {
        const parsed = JSON.parse(location);
        if (parsed?.latitude && parsed?.longitude) {
          return {
            lat: parseFloat(parsed.latitude),
            lng: parseFloat(parsed.longitude)
          };
        }
      } else {
        const country = sessionStorage.getItem('country');
        if (countryLatLngMap[country]) return countryLatLngMap[country];
      }
    } catch (e) {
      console.error("Error parsing location from sessionStorage", e);
    }
    return null;
  };

  const initialCenter = getStoredLocation() || defaultCenter;

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
    libraries: ["places"]
  });

  // Handle user clicking on map: update marker and reverse geocode address
  const handleMapClick = useCallback(async (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setMarkerPosition({ lat, lng });
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAP_API_KEY}`
      );
      const data = await response.json();

      if (data.status === "OK") {
        const result = data.results[0];
        const components = result.address_components;

        const getComponent = (type) =>
          components.find((comp) => comp.types.includes(type))?.long_name || "";

        const landmark =
          getComponent("point_of_interest") ||
          getComponent("premise") ||
          getComponent("establishment") ||
          getComponent("subpremise");

        setFormValues((prev) => ({
          ...prev,
          address: result.formatted_address,
          landmark: landmark,
          latitude: lat,
          longitude: lng,
        }));
        setIsPickedOrSelectLocation(true)
        setErr((prev) => ({
          ...prev,
          address: "",
          landmark: landmark ? "" : prev[`landmark`]
        }))
      }
    } catch (error) {
      console.error("Error fetching location details:", error);
    }
  }, [setFormValues]);

  if (!isLoaded) return <p>Loading Map...</p>;

  return (
    <div className="pin-location-map-container">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={markerPosition || initialCenter}
        zoom={markerPosition ? 16 : 10}
        onClick={handleMapClick}
      >
        {markerPosition && <Marker position={markerPosition} />}
      </GoogleMap>
    </div>
  );
};

export default CheckoutSectionMapLocationPicker;
